import React, { useState, useCallback, useLayoutEffect, useEffect, useMemo } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import * as Sentry from '@sentry/react';
import { Autocomplete, Input, Div, Icon, Text, Switch } from '@pinpoint/design-kit';

import Rbac from 'src/containers/rbac';
import { FileDrop, FrameworksDropdown } from 'src/components';
import CONSTANTS from 'src/store/constants';
import useFrameworksBasic from 'src/hooks/swr/useFrameworksBasic';
import usePlatformConfig from 'src/hooks/usePlatformConfig';
import BaseDialog from './base';

const defaultValues = {
  name: '',
  riskPercentage: '',
  predictionType: 'Actuarial Loss',
  typeDetail: '',
  description: '',
  hidden: false,
  internalValidationPercentage: 5,
  replacedOutcomes: false,
  replacedOutcomesFile: '',
  frameworkId: null,
  modelUseWeights: true,
  modelAggregateUser: true,
};
const menuItems = CONSTANTS.TAL_MODEL.PERILS;

export function CreateActuarialModel({
  isOpen,
  actions,
  meetsValidationMatchedThreshold,
  validationMatchedCount,
  tenant,
  refresh,
}) {
  const platformConfig = usePlatformConfig();
  const internalValidationRequired = !meetsValidationMatchedThreshold;

  // Local State
  const [perilList, setPerilList] = useState(''); // NOTE: store the list as a string for easy comparison
  const [perilIdxList, setPerilIdxList] = useState(''); // NOTE: store the list as a string for easy comparison
  const [complexMenuItems, setComplexMenuItems] = useState(CONSTANTS.TAL_MODEL.PERILS.join(',')); // NOTE: store the list as a string for easy comparison
  const [isPerilsOpen, setIsPerilsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(!meetsValidationMatchedThreshold || false);
  const [isInternalValidationOpen, setIsInternalValidationOpen] = useState(!meetsValidationMatchedThreshold || false);
  const [fileList, setFileList] = useState(/** @type {File[]} */ ([]));
  const bgColor = useMemo(() => (isPerilsOpen || isHovered ? 'shadeF2' : 'white'), [isPerilsOpen, isHovered]);
  const fontColor = useMemo(() => (isPerilsOpen || isHovered ? 'shade66' : 'shade99'), [isPerilsOpen, isHovered]);
  const [progress, setProgress] = useState([0]);

  // Form State
  const { handleSubmit, errors, control, setValue, watch, setError, clearErrors } = useForm({ defaultValues });
  const typeDetail = watch('typeDetail');
  const replacedOutcomes = watch('replacedOutcomes');
  const resetForm = useCallback(() => {
    setPerilList('');
    setPerilIdxList('');
    setComplexMenuItems(CONSTANTS.TAL_MODEL.PERILS.join(','));
    setIsSettingsOpen(false);
    setIsInternalValidationOpen(false);
    setFileList([]);
    clearErrors('replacedOutcomesFile');
    setValue('replacedOutcomes', false);
    setValue('modelUseWeights', true);
    setValue('modelAggregateUser', true);
  }, [clearErrors, setValue]);
  const handleProgress = useCallback((val) => {
    setProgress([val]);
  }, []);
  const onSubmit = async (data) => {
    const inputData = data;
    inputData.name = inputData.name.trim();
    inputData.internalValidationPercentage =
      internalValidationRequired || isInternalValidationOpen ? +inputData.internalValidationPercentage : 0;
    inputData.riskPercentage = Number(inputData.riskPercentage);
    if (inputData.hidden) inputData.hiddenByRole = 'ADMIN';
    else inputData.hiddenByRole = null;
    delete inputData.hidden;
    inputData.trainingType = 'catboost';
    delete inputData.replacedOutcomesFile;
    inputData.metadataPayload = {};
    if (inputData.description) {
      inputData.metadataPayload = {
        description: inputData.description,
      };
      delete inputData.description;
    }
    inputData.metadataPayload.modelUseWeights = inputData.modelUseWeights ?? defaultValues.modelUseWeights;
    inputData.metadataPayload.modelAggregateUser = inputData.modelAggregateUser ?? defaultValues.modelAggregateUser;
    if (replacedOutcomes && !fileList?.length) {
      setError('replacedOutcomesFile', {
        type: 'required',
        message: 'Please upload a replacement outcomes .csv file.',
      });
      return false;
    }

    if (replacedOutcomes && fileList?.length) {
      clearErrors('replacedOutcomesFile');
      const uploadFileName = `${crypto.randomUUID()}.csv`;
      const uploadPrefix = CONSTANTS.S3_PREFIX.ONPOINT_OUTPUT_REPLACEMENT;
      const environment = window.location.host.split?.('-')?.[0];
      const stage = ['stage', 'dev'].includes(environment) ? `${environment}-` : '';
      const uploadFileBucket = `${stage}ppp-client-data`;
      const uploadFileKey = `${tenant}/${uploadPrefix}/${uploadFileName}`;
      const url = await actions.handlers[0].before(uploadFileBucket, uploadFileKey);
      await actions.handlers[0].on(url, fileList[0], handleProgress);
      const replacedOutcomesS3 = {
        bucket: uploadFileBucket,
        key: uploadFileKey,
      };
      try {
        await actions.handlers[0].after({
          replacedOutcomes: replacedOutcomesS3,
          internalValidationPercentage: inputData.internalValidationPercentage,
        });
        inputData.metadataPayload = {
          ...inputData.metadataPayload,
          replacedOutcomes: replacedOutcomesS3,
        };
      } catch (err) {
        if (err.name === 'ApiError') {
          setError('replacedOutcomesFile', {
            type: 'manual',
            message: err.message,
          });
        } else console.error(err); // eslint-disable-line no-console
        return false;
      } finally {
        setProgress([0]);
        setFileList([]);
      }
    }
    actions.handlers[1].on(inputData);
    resetForm();
    actions.close.on();
    return true;
  };
  const formSubmit = handleSubmit(onSubmit);

  // Frameworks
  const frameworksQuery = useMemo(() => {
    const query = {
      filters: [],
      order_by: [
        {
          field: 'id',
          direction: 'asc',
        },
      ],
      fields: 'items{id,name,refreshes,regulationTags},*',
    };
    return query;
  }, []);

  const { data: frameworks = [] } = useFrameworksBasic({ query: frameworksQuery, config: { revalidateOnMount: true } });

  // Frameworks dropdown
  const [isFrameworksOpen, setIsFrameworksOpen] = useState(false);
  const [isFrameworksHovered, setIsFrameworksHovered] = useState(false);
  const frameworksBgColor = useMemo(
    () => (isFrameworksOpen || isFrameworksHovered ? 'shadeF2' : 'white'),
    [isFrameworksOpen, isFrameworksHovered]
  );
  const frameworksFontColor = useMemo(
    () => (isFrameworksOpen || isFrameworksHovered ? 'shade66' : 'shade99'),
    [isFrameworksOpen, isFrameworksHovered]
  );
  const setFrameworksHovered = useCallback(() => setIsFrameworksHovered(true), []);
  const setFrameworksNotHovered = useCallback(() => setIsFrameworksHovered(false), []);

  // Handlers
  const handleClick = useCallback(() => setIsPerilsOpen(!isPerilsOpen), [isPerilsOpen]);
  const setHovered = useCallback(() => setIsHovered(true), []);
  const setNotHovered = useCallback(() => setIsHovered(false), []);
  const handleSettingsToggle = useCallback(() => {
    if (internalValidationRequired) return;
    setIsSettingsOpen(!isSettingsOpen);
    if (isSettingsOpen) {
      setIsInternalValidationOpen(false);
    }
  }, [internalValidationRequired, isSettingsOpen]);
  const validateFile = useCallback(
    async (file) => {
      if (!file?.name) return;
      const fileMetadata = {
        name: file.name,
        type: file.type,
        size: file.size,
      };
      if (!file.name.slice(-4).toLowerCase().includes('csv')) {
        Sentry.captureException(
          `File uploaded by tenant ${tenant} was not a csv. Meta: ${JSON.stringify(fileMetadata, null, 2)}.`
        );
        setFileList([]);
        setError('replacedOutcomesFile', {
          type: 'manual',
          message: `File ${file.name} was not a csv file.`,
        });
        return;
      }
      setFileList([file]);
      clearErrors('replacedOutcomesFile');
    },
    [clearErrors, setError, tenant]
  );
  const handleFileDrop = useCallback(
    (item, monitor) => {
      if (monitor) {
        const { files: _files } = monitor.getItem();
        if (_files?.length) {
          validateFile(_files[0]);
        }
      }
      return null;
    },
    [validateFile]
  );

  const handleFileSelect = useCallback(
    (_files) => {
      if (_files?.length) {
        validateFile(_files[0]);
      }
      return null;
    },
    [validateFile]
  );
  const handleSelection = useCallback(
    (index) => {
      const nextPeril = menuItems[index];
      const perils = new Set(perilList.split(', ').filter((elem) => elem));
      const perilsIdx = new Set(
        perilIdxList
          .split(',')
          .filter((elem) => elem)
          .map((idx) => +idx)
      );
      if (perils.has(nextPeril)) {
        perils.delete(nextPeril);
        perilsIdx.delete(index);
      } else {
        perils.add(nextPeril);
        perilsIdx.add(index);
      }
      const nextPerilList = [...perils].filter((elem) => elem).join(', ');
      const nextPerilIdxList = [...perilsIdx].map((elem) => `${elem}`).join(',');
      const nextPerilsIdx = [...perilsIdx];
      const nextMenuItems = Array.from(menuItems);
      nextPerilsIdx.forEach((idx) => {
        nextMenuItems[idx] = `${String.fromCodePoint(10003)} ${nextMenuItems[idx]}`;
      });
      setPerilList(nextPerilList);
      setPerilIdxList(nextPerilIdxList);
      setComplexMenuItems(nextMenuItems.join(','));
      setValue('typeDetail', nextPerilList);
    },
    [perilList, setValue, perilIdxList]
  );

  useEffect(() => {
    setIsSettingsOpen(internalValidationRequired);
    setIsInternalValidationOpen(internalValidationRequired);
  }, [internalValidationRequired, isOpen]);

  useEffect(() => {
    if (!replacedOutcomes) {
      clearErrors('replacedOutcomesFile');
    }
  }, [clearErrors, replacedOutcomes]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <BaseDialog
      isOpen={isOpen}
      modalWidth={isSettingsOpen ? '70%' : '40%'}
      hideCloseIcon
      actions={{
        ...actions,
        cancel: {
          ...actions.cancel,
          before: resetForm,
        },
        close: {
          ...actions.close,
          before: resetForm,
        },
        handlers: [
          {
            on: formSubmit,
          },
        ],
      }}
      items={
        /** @type {any} */ ({
          buttons: /** @type {{ icon?: any, text: string, textSize?: string, close: boolean }[]} */ ([
            {
              text: 'CREATE',
              textSize: 'body',
              close: false,
            },
          ]),
          content: /** @type {any[]} */ (
            [
              <Div key="form-0" d="flex" align="center">
                {!meetsValidationMatchedThreshold && (
                  <>
                    <Icon
                      name="AlertSolid"
                      color="marigold"
                      viewBox="0 0 24 24"
                      size="1.5rem"
                      m={{ r: '0.5rem', b: '1rem' }}
                    />
                    <Text
                      textSize="input"
                      textWeight="500"
                      textColor="marigold"
                      m={{ b: '1rem' }}
                      style={{ lineHeight: '1.25rem' }}
                    >
                      Caution: This model does not contain sufficient blind-validation records. Internal validation is
                      required in this case.
                    </Text>
                  </>
                )}
              </Div>,
              <Div key="form-1" d="flex" flexDir="column">
                <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
                  Enter a name for your model
                </Text>
                <Controller
                  borderColor="shade66"
                  name="name"
                  as={Input}
                  control={control}
                  rules={{
                    required: 'Name is required',
                    pattern: {
                      value: /^[^0-9\s\W]/,
                      message: 'Name must start with a letter - not a number, special character, or space.',
                    },
                  }}
                  m={{ t: '.25rem' }}
                  h="1.875rem"
                />
                <ErrorMessage
                  errors={errors}
                  name="name"
                  render={({ message }) => {
                    if (![undefined, null, ''].includes(message)) {
                      return (
                        <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                          {message}
                        </Text>
                      );
                    }
                    return null;
                  }}
                />
              </Div>,
              <Div key="form-2" d="flex" flexDir="column">
                <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
                  Select one or more peril(s)
                </Text>
                <Controller
                  borderColor="shade66"
                  name="typeDetail"
                  as={Autocomplete}
                  control={control}
                  rules={{
                    required: 'Peril(s) is required',
                  }}
                  m={{ t: '.25rem' }}
                  h="1.875rem"
                  w="100%"
                  isOpen={isPerilsOpen}
                  onClick={handleClick}
                  onMouseEnter={setHovered}
                  onMouseLeave={setNotHovered}
                  onSelection={handleSelection}
                  isHovered={isHovered}
                  menu={complexMenuItems.split(',')}
                  DropdownButtonOptions={{
                    h: '1.875rem',
                    w: '2rem',
                    bg: bgColor,
                    focusBg: bgColor,
                    hoverBg: bgColor,
                    rounded: { tr: 'md', br: 'md' },
                    border: { y: '1px solid', r: '1px solid', l: '0' },
                    borderColor: 'shade66',
                    align: 'center',
                    justify: 'center',
                    d: 'flex',
                    p: '0',
                    transition: 'all 0.2s ease-in-out',
                  }}
                  inputStyle={{
                    h: 'calc(1.875rem-1px)',
                    textColor: fontColor,
                    textWeight: '500',
                    bg: bgColor,
                    focusBg: bgColor,
                    hoverBg: bgColor,
                    style: {
                      transition: 'all 0.2s ease-in-out',
                      font: 'inherit',
                    },
                  }}
                  DivOptions={{
                    rounded: isPerilsOpen ? { tl: 'md', bl: '0', br: '0', tr: '0' } : { l: 'md', r: '0' },
                    border: isPerilsOpen ? { x: '1px solid', t: '1px solid', b: '0' } : '1px solid',
                    borderColor: 'shade66',
                    bg: bgColor,
                    hoverBg: bgColor,
                    style: {
                      transition: 'all 0.2s ease-in-out',
                    },
                  }}
                  menuItemOptions={{
                    p: { y: '.25rem', x: '.75rem' },
                    textWeight: '500',
                    bg: 'shadeF2',
                    hoverBg: 'white',
                  }}
                  menuOptions={{
                    border: { b: '1px solid', x: '1px solid', t: '0' },
                    rounded: { bl: 'md', br: 'md', tr: '0', tl: '0' },
                    pos: 'absolute',
                    shadow: 4,
                    minW: 'calc(100% - 2rem)',
                    maxW: 'calc(100% - 2rem)',
                    style: {
                      transition: 'all 0.2s ease-in-out',
                    },
                    maxH: '250px',
                    overflow: 'auto',
                    textColor: fontColor,
                    m: { t: '-2px' },
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="typeDetail"
                  render={({ message }) => {
                    if (![undefined, null, ''].includes(message)) {
                      return (
                        <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                          {message}
                        </Text>
                      );
                    }
                    return null;
                  }}
                />
              </Div>,
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                {typeDetail && typeDetail.toLowerCase().includes('other') && (
                  <Div d="flex" flexDir="column">
                    <Div>
                      <Text d="inline-block" textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
                        Description
                      </Text>
                      <Icon
                        viewBox="0 0 24 24"
                        d="inline-block"
                        name="InfoSerif"
                        m={{ x: '0.5rem' }}
                        style={{ verticalAlign: 'text-bottom' }}
                        size=".875rem"
                        data-tip='Describe "other" peril(s)'
                      />
                    </Div>
                    <Controller
                      name="description"
                      as={Input}
                      m={{ t: '.25rem' }}
                      h="1.875rem"
                      borderColor="shade66"
                      control={control}
                      rules={{
                        required: 'Description is required',
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="description"
                      render={({ message }) => {
                        if (![undefined, null, ''].includes(message)) {
                          return (
                            <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                              {message}
                            </Text>
                          );
                        }
                        return null;
                      }}
                    />
                  </Div>
                )}
              </>,
              <Div key="form-3" d="flex" flexDir="row">
                <Div>
                  <Div>
                    <Text d="inline-block" textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
                      Dataset claimant frequency
                    </Text>
                    <Icon
                      viewBox="0 0 24 24"
                      d="inline-block"
                      name="InfoSerif"
                      m={{ x: '0.5rem' }}
                      style={{ verticalAlign: 'text-bottom' }}
                      size=".875rem"
                      data-tip="Please enter the proportion of records with one or more claims, as a percentage of all records in the dataset"
                    />
                  </Div>
                  <Controller
                    name="riskPercentage"
                    as={Input}
                    m={{ t: '.25rem' }}
                    rounded={{ l: 'md' }}
                    h="1.875rem"
                    type="number"
                    borderColor="shade66"
                    suffix={
                      <Div
                        h="1.875rem"
                        w="1.375rem"
                        border={{ y: '1px solid', r: '1px solid' }}
                        borderColor="shade66"
                        rounded={{ tr: 'md', br: 'md' }}
                        p={{ x: '0.25rem' }}
                        align="center"
                        justify="center"
                        d="flex"
                      >
                        <Icon m="0px" name="Percent" color="grey" size=".75rem" viewBox="0 0 12 12" />
                      </Div>
                    }
                    control={control}
                    rules={{
                      required: 'Risk % is required',
                      min: { value: 0, message: 'The risk % cannot be lesser than 0%' },
                      max: { value: 100, message: 'The risk % cannot be greater than 100%' },
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="riskPercentage"
                    render={({ message }) => {
                      if (![undefined, null, ''].includes(message)) {
                        return (
                          <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                            {message}
                          </Text>
                        );
                      }
                      return null;
                    }}
                  />
                </Div>
              </Div>,
              <Div key="form-4" d="flex" flexDir="column">
                <Div>
                  <Text d="inline-block" textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
                    Select a regulatory framework
                  </Text>
                  <Icon
                    viewBox="0 0 24 24"
                    d="inline-block"
                    name="InfoSerif"
                    m={{ x: '0.5rem' }}
                    style={{ verticalAlign: 'text-bottom' }}
                    size=".875rem"
                    data-tip="Only for use with filed pricing; type in the applicable state code(s) or line(s) of business to filter options"
                  />
                </Div>
                <Controller
                  name="frameworkId"
                  control={control}
                  render={({ onChange, value }) => (
                    <FrameworksDropdown
                      data-test="model-frameworks-dropdown"
                      dropDownStyle={{
                        DropdownButtonOptions: {
                          h: '1.875rem',
                          w: '2rem',
                          bg: frameworksBgColor,
                          focusBg: frameworksBgColor,
                          hoverBg: frameworksBgColor,
                          rounded: { tr: 'md', br: 'md' },
                          border: { y: '1px solid', r: '1px solid', l: '0' },
                          borderColor: 'shade66',
                          align: 'center',
                          justify: 'center',
                          d: 'flex',
                          p: '0',
                          transition: 'all 0.2s ease-in-out',
                        },
                        inputStyle: {
                          h: 'calc(1.875rem-1px)',
                          flexGrow: '1',
                          p: { l: '0' },
                          rounded: 'none',
                          textColor: frameworksFontColor,
                          textWeight: '500',
                          bg: frameworksBgColor,
                          focusBg: frameworksBgColor,
                          hoverBg: frameworksBgColor,
                          style: {
                            transition: 'all 0.2s ease-in-out',
                            font: 'inherit',
                          },
                          'data-test': 'model-frameworks-dropdown-input',
                        },
                        DivOptions: {
                          rounded: isFrameworksOpen ? { tl: 'md', bl: '0', br: '0', tr: '0' } : { l: 'md', r: '0' },
                          border: isFrameworksOpen ? { x: '1px solid', t: '1px solid', b: '0' } : '1px solid',
                          borderColor: 'shade66',
                          bg: frameworksBgColor,
                          hoverBg: frameworksBgColor,
                          style: {
                            transition: 'all 0.2s ease-in-out',
                          },
                          p: { l: '0.75rem' },
                        },
                        menuItemOptions: {
                          d: 'flex',
                          align: 'center',
                          p: { y: '.25rem', x: '.75rem' },
                          textWeight: '500',
                          bg: 'shadeF2',
                          hoverBg: 'white',
                          'data-test': 'model-frameworks-dropdown-menu-item',
                        },
                        menuOptions: {
                          border: { b: '1px solid', x: '1px solid', t: '0' },
                          rounded: { bl: 'md', br: 'md', tr: '0', tl: '0' },
                          pos: 'absolute',
                          shadow: 4,
                          minW: 'calc(100% - 2rem)',
                          maxW: 'calc(100% - 2rem)',
                          style: {
                            transition: 'all 0.2s ease-in-out',
                          },
                          maxH: '250px',
                          overflow: 'auto',
                          textColor: frameworksFontColor,
                          m: { t: '-2px' },
                        },
                      }}
                      frameworks={frameworks.filter((framework) =>
                        refresh
                          ? platformConfig.isUpgradedPlatform(refresh?.platform) &&
                            framework.refreshes.includes(refresh?.name)
                          : false
                      )}
                      framework={value}
                      onSelectFramework={(framework) => onChange(framework)}
                      defaultText="None / Not Applicable"
                      isOpen={isFrameworksOpen}
                      onClick={() => setIsFrameworksOpen(!isFrameworksOpen)}
                      onMouseEnter={setFrameworksHovered}
                      onMouseLeave={setFrameworksNotHovered}
                    />
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name="frameworkId"
                  render={({ message }) => {
                    if (![undefined, null, ''].includes(message)) {
                      return (
                        <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                          {message}
                        </Text>
                      );
                    }
                    return null;
                  }}
                />
              </Div>,
              <Div key="form-5" d="flex" flexDir="column">
                <Rbac roles={CONSTANTS.ROLES.slice(0, 4)}>
                  <Div m={{ t: '1.5rem' }} d="flex" flexDir="column" data-debug="settings-container">
                    <Div d="flex" flexDir="row" data-debug="settings-sub-container">
                      <Div d="flex" align="center" justify="center" flexWrap="nowrap" data-debug="settings-toggle">
                        <Text
                          d="flex"
                          align="center"
                          justify="center"
                          textSize="input"
                          textWeight="500"
                          textColor="shade66"
                        >
                          Advanced Settings
                        </Text>
                        <Div data-test="advanced-settings-toggle" onClick={handleSettingsToggle} cursor="pointer">
                          <Switch
                            checked={isSettingsOpen}
                            // @ts-ignore
                            d="inline-block"
                            m={{ x: '.5rem' }}
                          />
                        </Div>
                      </Div>
                      {isSettingsOpen && (
                        <Div d="flex" align="center" data-test="settings" data-debug="settings">
                          <Controller
                            name="hidden"
                            h="1.875rem"
                            type="checkbox"
                            control={control}
                            render={({ onChange, value }) => (
                              <Div
                                d="flex"
                                p={{ l: '1rem' }}
                                m={{ l: '0.5rem' }}
                                style={{ borderLeft: '1px black solid' }}
                                align="center"
                                justify="center"
                                flexDir="row-reverse"
                              >
                                <Text textSize="input" textWeight="500" textColor="shade66" d="inline-block">
                                  Hidden
                                </Text>
                                <Div data-test="hidden-toggle" onClick={() => onChange(!value)} cursor="pointer">
                                  <Switch
                                    checked={value}
                                    // @ts-ignore
                                    d="inline-block"
                                    m={{ x: '.5rem' }}
                                  />
                                </Div>
                                <Text textSize="input" textWeight="500" textColor="shade66" d="inline-block">
                                  Visible
                                </Text>
                              </Div>
                            )}
                          />
                          <Controller
                            name="replacedOutcomes"
                            h="1.875rem"
                            type="checkbox"
                            control={control}
                            render={({ onChange, value }) => (
                              <Div
                                d="flex"
                                p={{ l: '1rem' }}
                                m={{ l: '0.5rem' }}
                                style={{ borderLeft: '1px black solid' }}
                                align="center"
                                justify="center"
                                flexDir="row-reverse"
                              >
                                <Icon
                                  viewBox="0 0 24 24"
                                  d="inline-block"
                                  name="InfoSerif"
                                  m={{ l: '0.5rem' }}
                                  style={{ verticalAlign: 'text-bottom' }}
                                  size=".875rem"
                                  data-tip="Replaces outcomes columns for new model"
                                />
                                <Text textSize="input" textWeight="500" textColor="shade66" d="inline-block">
                                  Replace Outcomes
                                </Text>
                                <Div data-test="replacement-toggle" onClick={() => onChange(!value)} cursor="pointer">
                                  <Switch
                                    checked={value}
                                    // @ts-ignore
                                    d="inline-block"
                                    m={{ x: '.5rem' }}
                                  />
                                </Div>
                              </Div>
                            )}
                          />
                          <Controller
                            name="modelUseWeights"
                            h="1.875rem"
                            type="checkbox"
                            control={control}
                            render={({ onChange, value }) => (
                              <Div
                                d="flex"
                                p={{ l: '1rem' }}
                                m={{ l: '0.5rem' }}
                                style={{ borderLeft: '1px black solid' }}
                                align="center"
                                justify="center"
                                flexDir="row-reverse"
                              >
                                <Text textSize="input" textWeight="500" textColor="shade66" d="inline-block">
                                  Model Use Weights
                                </Text>
                                <Div data-test="weight-toggle" onClick={() => onChange(!value)} cursor="pointer">
                                  <Switch
                                    checked={value}
                                    // @ts-ignore
                                    d="inline-block"
                                    m={{ x: '.5rem' }}
                                  />
                                </Div>
                              </Div>
                            )}
                          />
                          <Controller
                            name="modelAggregateUser"
                            h="1.875rem"
                            type="checkbox"
                            control={control}
                            render={({ onChange, value }) => (
                              <Div
                                d="flex"
                                p={{ l: '1rem' }}
                                m={{ l: '0.5rem' }}
                                style={{ borderLeft: '1px black solid' }}
                                align="center"
                                justify="center"
                                flexDir="row-reverse"
                              >
                                <Icon
                                  viewBox="0 0 24 24"
                                  d="inline-block"
                                  name="InfoSerif"
                                  m={{ l: '0.5rem' }}
                                  style={{ verticalAlign: 'text-bottom' }}
                                  size=".875rem"
                                  data-tip="When on, merge CRM records before training. Turn off to model CRM records individually."
                                />
                                <Text textSize="input" textWeight="500" textColor="shade66" d="inline-block">
                                  Aggregate by user
                                </Text>
                                <Div data-test="weight-toggle" onClick={() => onChange(!value)} cursor="pointer">
                                  <Switch
                                    checked={value}
                                    // @ts-ignore
                                    d="inline-block"
                                    m={{ x: '.5rem' }}
                                  />
                                </Div>
                              </Div>
                            )}
                          />
                          <Rbac roles={CONSTANTS.ROLES.slice(0, 4)}>
                            <Div
                              d="flex"
                              p={{ l: '1rem' }}
                              m={{ l: '1rem' }}
                              style={{ borderLeft: '1px black solid' }}
                              align="center"
                              justify="center"
                            >
                              <Text textSize="input" textWeight="500" textColor="shade66" d="inline-block">
                                Internal Validation
                              </Text>
                              <Icon
                                viewBox="0 0 24 24"
                                d="inline-block"
                                name="InfoSerif"
                                m={{ l: '0.5rem' }}
                                style={{ verticalAlign: 'text-bottom' }}
                                size=".875rem"
                                data-tip="Turning on the internal-validation setting will hold out a percentage of records from the labeled training set and use these in place of any blind validation records provided by the client. If there are any blind validation records in the dataset, no predictions will be written out to them."
                              />
                              <Div
                                data-test="validation-toggle"
                                onClick={() => {
                                  if (!internalValidationRequired) {
                                    setIsInternalValidationOpen(!isInternalValidationOpen);
                                  }
                                }}
                                cursor={internalValidationRequired ? 'not-allowed' : 'pointer'}
                                data-tip={
                                  internalValidationRequired
                                    ? 'Internal validation is required because there are not enough blind-validation records.'
                                    : undefined
                                }
                              >
                                <Switch
                                  checked={isInternalValidationOpen}
                                  // @ts-ignore
                                  d="inline-block"
                                  m={{ x: '.5rem' }}
                                />
                              </Div>
                            </Div>
                            {isInternalValidationOpen && (
                              <Div d="flex" align="center">
                                <Controller
                                  name="internalValidationPercentage"
                                  as={Input}
                                  rounded={{ l: 'md' }}
                                  h="1.5rem"
                                  w="4rem"
                                  p={{ l: '0.25rem', r: '0rem' }}
                                  type="number"
                                  borderColor="shade66"
                                  min={0}
                                  max={100}
                                  suffix={
                                    <Div
                                      h="1.5rem"
                                      w="1.375rem"
                                      border={{ y: '1px solid', r: '1px solid' }}
                                      borderColor="shade66"
                                      rounded={{ tr: 'md', br: 'md' }}
                                      p={{ x: '0.25rem' }}
                                      align="center"
                                      justify="center"
                                      d="flex"
                                    >
                                      <Icon m="0px" name="Percent" color="grey" size=".75rem" viewBox="0 0 12 12" />
                                    </Div>
                                  }
                                  control={control}
                                  rules={{
                                    required: 'Holdout % is required',
                                    min: { value: 0, message: 'The holdout % cannot be less than 0%' },
                                    max: { value: 100, message: 'The holdout % cannot be greater than 100%' },
                                    validate: {
                                      positive: (value) => Number(value) > 0 || 'The holdout % must be greater than 0%',
                                      underMatchedCount: (value) => {
                                        if (replacedOutcomes) {
                                          return true;
                                        }
                                        return (
                                          (value / 100) * validationMatchedCount >=
                                            CONSTANTS.SEGMENT.MIN_VALIDATION_MATCHES ||
                                          `The internal-validation holdout must contain at least 1,000 records (${
                                            value / 100
                                          } x ${validationMatchedCount} = ${(
                                            (value / 100) *
                                            validationMatchedCount
                                          ).toFixed(0)})`
                                        );
                                      },
                                    },
                                  }}
                                />
                                <Icon
                                  viewBox="0 0 24 24"
                                  d="inline-block"
                                  name="InfoSerif"
                                  m={{ x: '0.5rem' }}
                                  style={{ verticalAlign: 'text-bottom' }}
                                  size=".875rem"
                                  data-tip="Select the percentage of matched, labeled records to hold out from training, to use instead for calibration (and validation) purposes."
                                />
                              </Div>
                            )}
                          </Rbac>
                        </Div>
                      )}
                    </Div>
                    <ErrorMessage
                      errors={errors}
                      name="internalValidationPercentage"
                      render={({ message }) => {
                        if (![undefined, null, ''].includes(message)) {
                          return (
                            <Text textColor="danger800" textSize="caption" m={{ t: '0.25rem' }}>
                              {message}
                            </Text>
                          );
                        }
                        return null;
                      }}
                    />
                  </Div>
                </Rbac>
              </Div>,
            ].concat(
              !replacedOutcomes
                ? []
                : [
                    <Div key="form-5" id="form-5" m={{ t: '1rem' }}>
                      <Div d="flex" justify="flex-start">
                        <FileDrop
                          boxStyle={{ w: '20rem', h: '14rem', d: 'flex', justify: 'center' }}
                          onDrop={handleFileDrop}
                          onSelect={handleFileSelect}
                          droppedFiles={fileList}
                          progress={progress}
                          setDroppedFiles={setFileList}
                        />
                        <Div tag="ol" textSize="subtitle" p={{ l: '1rem' }} textWeight="500" textColor="shade66">
                          <Div tag="li">1. Download the relevant Post-Match Input Distribution .csv file.</Div>
                          <Div tag="li" m={{ t: '.5rem' }}>
                            2. Create a new upload .csv that includes the same list of CRM IDs, or a subset of these. No
                            new CRM IDs may be added.
                          </Div>
                          <Div tag="li" m={{ t: '.5rem' }}>
                            3. Your new upload must also include the standard TG_ columns; any other TG_ columns will be
                            passed through but not used in modeling. You may change any values in the TG_ cells.
                          </Div>
                          <Div tag="li" m={{ t: '.5rem' }}>
                            4. If relevant, include any blind validation rows.
                          </Div>
                        </Div>
                      </Div>
                      <ErrorMessage
                        errors={errors}
                        name="replacedOutcomesFile"
                        render={({ message }) => {
                          if (![undefined, null, ''].includes(message)) {
                            return (
                              <Text textColor="danger800" textSize="caption" m={{ t: '0.25rem' }}>
                                {message}
                              </Text>
                            );
                          }
                          return null;
                        }}
                      />
                    </Div>,
                  ]
            )
          ),
        })
      }
      title={{
        text: 'Generate Actuarial Model',
        backgroundColor: 'talLime',
        color: 'white',
        textSize: 'title',
        icon: (
          <Icon
            name="ActuarialModel"
            viewBox="0 0 12 12"
            size="2.375rem"
            color="white"
            alignSelf="center"
            justifySelf="center"
          />
        ),
      }}
    />
  );
}

CreateActuarialModel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    cancel: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    close: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    handlers: PropTypes.arrayOf(
      PropTypes.shape({
        before: PropTypes.func,
        on: PropTypes.func.isRequired,
        after: PropTypes.func,
      })
    ),
  }).isRequired,
  meetsValidationMatchedThreshold: PropTypes.bool.isRequired,
  validationMatchedCount: PropTypes.number.isRequired,
  tenant: PropTypes.string.isRequired,
  refresh: PropTypes.shape({
    name: PropTypes.string.isRequired,
    platform: PropTypes.number.isRequired,
  }).isRequired,
};

export default CreateActuarialModel;
